<style scoped>
.top--20 {
  top: -5rem;
}
</style>
<template>
  <div class="forms-app">
    <div class="flex">
      <NavigationComponent
        v-if="!embed"
        class="fixed bg-white w-full px-4 border-b border-gray-200 z-10"
        :logo="logo"
        :saveState="saveState"
      />
      <div class="flex max-w-6xl mx-auto py-20 px-4 sm:px-6 lg:px-8">
        <TableOfContents
          v-if="toc && toc.length > 0"
          class="hidden sm:block fixed h-full w-64 overflow-y-auto pr-8"
          :toc="toc"
        />
        <div :class="{ 'sm:ml-64': toc && toc.length > 0 }">
          <div v-if="submitted">
            <h1
              class="text-3xl leading-8 font-semibold text-gray-900 sm:text-3xl sm:leading-9"
            >
              {{ title }}
            </h1>
          </div>
          <div aria-live="polite" aria-atomic="true">
            <template v-if="submitted">
              <p class="text-sm font-medium">
                {{
                  thankYouMessage
                    ? thankYouMessage
                    : $t('form.default_thank_you_message')
                }}
              </p>

              <template v-if="isResponseDownloadable">
                <button
                  @click="downloadFile('xlsx')"
                  class="btn-primary mt-6"
                >
                  {{ $t('form.download_excel') }}
                </button>
                {{ ' ' }}
                <button
                  @click="downloadFile('docx')"
                  class="btn-primary mt-6"
                >
                  {{ $t('form.download_word') }}
                </button>
                {{ ' ' }}
                <button
                  @click="downloadFile('pdf')"
                  class="btn-primary mt-6"
                >
                  {{ $t('form.download_pdf') }}
                </button>
              </template>
            </template>
          </div>
          <div v-if="isClosed">
            <h1
              class="text-3xl leading-8 font-semibold text-gray-900 sm:text-3xl sm:leading-9"
            >
              {{ title }}
            </h1>
            <p>{{ $t('form.cannot_be_completed') }}</p>
            <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-600">
              {{ $t('form.start_time', { start_time: openingDateFormatted }) }}
            </p>
            <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-600">
              {{
                $t('form.closing_time', { closing_time: closingDateFormatted })
              }}
            </p>
          </div>
          <div v-if="!submitted && !isClosed">
            {{ resetCount() }}
            <h1
              class="text-3xl leading-8 font-semibold text-gray-900 sm:text-3xl sm:leading-9"
            >
              {{ title }}
            </h1>
            <p
              v-if="respondent"
              class="mt-1 max-w-2xl text-sm leading-5 text-gray-600"
            >
              {{ $t('form.form_for', { respondent }) }}
            </p>
            <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-600">
              {{ $t('form.fields_with_asterisk_required') }}
            </p>

            <ValidationObserver v-slot="{ validate }">
              <form
                ref="form"
                @submit="(e) => onSubmit(e, validate)"
                id="form"
                method="POST"
                enctype="multipart/form-data"
                novalidate
              >
                <input
                  v-if="csrftokenToUse"
                  type="hidden"
                  name="csrfmiddlewaretoken"
                  :value="csrftokenToUse"
                />
                <div v-for="(field, i) in fields" v-bind:key="field.id">
                  <a
                    v-if="['h2', 'h3', 'h4'].includes(field.element)"
                    :id="field.value"
                    class="block relative invisible top--20 anchor"
                  />
                  <h2
                    v-if="field.element == 'h2'"
                    class="text-2xl leading-6 font-semibold text-gray-900 mt-20 border-b border-gray-200 pb-3 mb-3"
                  >
                    {{ field.value }}
                  </h2>
                  <h3
                    v-if="field.element == 'h3'"
                    class="text-lg leading-6 font-medium text-gray-700 mt-9"
                  >
                    {{ field.value }}
                  </h3>
                  <h4
                    v-if="field.element == 'h4'"
                    class="text-sm leading-5 font-medium text-gray-700 mt-8"
                  >
                    {{ field.value }}
                  </h4>
                  <VueMarkdown
                    v-if="field.element == 'p'"
                    class="mt-6 sm:mt-5 max-w-2xl text-sm leading-5 prose max-w-none"
                    :source="field.value"
                  />
                  <div v-if="field.element == 'field'" class="mt-6 sm:mt-5">
                    <FieldComponent
                      v-if="isVisible(field)"
                      :number="i + 1"
                      :field="field"
                      :response="response"
                      :csrftoken="csrftokenToUse"
                      :formsAppOrigin="formsAppOrigin"
                      :disabled="preview"
                      @change="onChange"
                    />
                  </div>
                </div>

                <div v-if="!preview" class="mt-8 pt-5">
                  <button type="submit" class="btn-primary">
                    {{ $t('form.submit') }}
                  </button>
                </div>
              </form>
            </ValidationObserver>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import VueMarkdown from 'vue-markdown-render';
import { ValidationObserver } from 'vee-validate';
import jsonLogic from 'json-logic-js';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

import NavigationComponent from './NavigationComponent';
import TableOfContents from './TableOfContents';
import FieldComponent from './FieldComponent.vue';

let counted = 0;

export default {
  name: 'FormComponent',
  components: {
    VueMarkdown,
    ValidationObserver,
    FieldComponent,
    TableOfContents,
    NavigationComponent,
  },
  data() {
    return {
      saveState: 'idle',
      error: false,
      submitted: false,
      currentResponse: this.response,
      localCsrftoken: null,
    };
  },
  async created() {
    this.setLang();
    this.fetchCsrfToken();
  },
  computed: {
    csrftokenToUse() {
      return this.csrftoken || this.localCsrftoken;
    },
    formsAppOrigin() {
      // Extract the origin (root URL) from baseUrl
      // baseUrl might be '/api/v1' or 'https://forms.example.com/api/v1'
      // We need the root URL for endpoints like /csrf/ and /attachments/create
      // (which are not under /api/v1/)
      if (!this.baseUrl) {
        return '';
      }

      // If baseUrl is a full URL, extract the origin
      if (
        this.baseUrl.startsWith('http://') ||
        this.baseUrl.startsWith('https://')
      ) {
        try {
          const url = new URL(this.baseUrl);
          return url.origin;
        } catch (e) {
          // If URL parsing fails, try to extract origin manually
          const match = this.baseUrl.match(new RegExp('^(https?://[^/]+)'));
          return match ? match[1] : '';
        }
      }

      // If baseUrl is a relative path like '/api/v1', return empty string for relative URLs
      return '';
    },
    openingDateFormatted() {
      return format(new Date(this.openingDate), 'dd-MM-yyyy HH:mm', {
        locale: nl,
      });
    },
    closingDateFormatted() {
      return format(new Date(this.closingDate), 'dd-MM-yyyy HH:mm', {
        locale: nl,
      });
    },
  },
  methods: {
    async fetchCsrfToken() {
      // If no CSRF token is provided (e.g., in embedded mode), fetch it from
      // the server.
      if (!this.csrftoken) {
        try {
          const csrfUrl = `${this.formsAppOrigin}/csrf/`;
          const csrfResponse = await fetch(csrfUrl, {
            credentials: 'include',
          });
          const csrfData = await csrfResponse.json();
          this.localCsrftoken = csrfData.csrfToken;
        } catch (e) {
          console.error('Failed to fetch CSRF token:', e);
        }
      }
    },
    setLang() {
      this.$i18n.locale = this.language?.toLowerCase();
    },
    resetCount() {
      counted = 0;
    },
    async onSubmit(e, validate) {
      const isValid = await validate();

      if (this.embed) {
        e.preventDefault();

        if (isValid) {
          const data = Object.fromEntries(new FormData(e.target));

          const result = await fetch(`${this.baseUrl}/responses/`, {
            method: 'POST',
            credentials: 'include',
            body: JSON.stringify({
              form: this.id,
              data: data,
            }),
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.csrftokenToUse,
            },
          });

          if (result.ok) {
            this.submitted = true;
            this.error = false;
          } else {
            this.submitted = false;
            this.error = true;
          }
        }
      }

      if (!isValid) {
        e.preventDefault();

        const invalid = this.$refs.form.querySelector('[aria-invalid="true"]');
        if (invalid) {
          invalid.focus();
        }
      }
    },
    count() {
      counted += 1;
      return counted;
    },
    async onChange(e) {
      this.$set(this.currentResponse, e.target.id, e.target.value);

      if (this.code) {
        this.saveState = 'saving';

        try {
          await fetch('', {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.csrftokenToUse,
            },
            body: JSON.stringify({
              [e.target.id]: e.target.value,
            }),
          });
        } catch (e) {
          console.error('Could not save change', e);
          this.saveState = 'error';
          return;
        }

        this.saveState = 'idle';
      }
    },
    isVisible(field) {
      if (!field.conditional) {
        return true;
      }

      if (field.conditional.show) {
        return jsonLogic.apply(
          field.conditional.show.when,
          this.currentResponse,
        );
      }

      if (field.conditional.hide) {
        return !jsonLogic.apply(
          field.conditional.hide.when,
          this.currentResponse,
        );
      }

      return true;
    },
    /**
     * Download response as file. CSRF token is included in the headers.
     * 
     * In embedded mode, session is stored in third-party cookies. When you open
     * a link in a new tab and it becomes first-party, this third-party cookie
     * is lost. Therefore, we have to get it with fetch() like the rest of the
     * requests.
     * 
     * @param format 'pdf', 'docx', 'xlsx'
     */
    async downloadFile(format) {
      try {
        const url = `${this.formsAppOrigin}/forms/${this.id}/fill/download/${format}`;
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'X-CSRFToken': this.csrftokenToUse,
          },
        });

        if (!response.ok) {
          throw new Error(`Download failed: ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `form-${this.id}.${format}`;
        a.click();
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('Error downloading file:', error);
      }
    },
  },
  props: {
    id: Number,
    baseUrl: String,
    toc: Array,
    title: String,
    language: String,
    logo: String,
    fields: Array,
    isClosed: Boolean,
    openingDate: String,
    closingDate: String,
    response: Object,
    respondent: String,
    csrftoken: String,
    code: String,
    preview: Boolean,
    embed: Boolean,
    thankYouMessage: String,
    isResponseDownloadable: Boolean,
  },
};
</script>
